import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import DiaryChart from './DiaryChart';
import { routineLabel } from './diaryScales';
import './Diary.css';

const RANGES = [
  { days: 28, label: 'Last 4 weeks' },
  { days: 56, label: 'Last 8 weeks' },
  { days: 84, label: 'Last 12 weeks' },
];
const DAY_MS = 24 * 60 * 60 * 1000;
const iso = (d) => d.toISOString().slice(0, 10);
const secs = (date) => Date.parse(`${date}T00:00:00Z`) / 1000;
const fmt = (n) => (n == null ? '–' : String(n));
const ENERGY_MOOD_RANGE = [1, 5];

const defaultFrom = () => iso(new Date(Date.now() - 27 * DAY_MS));

export default function DiarySummary({ gymnastId }) {
  const [range, setRange] = useState('28'); // a number of days, or 'custom'
  const [customFrom, setCustomFrom] = useState(defaultFrom);
  const [customTo, setCustomTo] = useState(() => iso(new Date()));
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let from;
    let to;
    if (range === 'custom') {
      if (!customFrom || !customTo || customFrom > customTo) return undefined;
      from = customFrom;
      to = customTo;
    } else {
      const now = new Date();
      to = iso(now);
      from = iso(new Date(now.getTime() - (Number(range) - 1) * DAY_MS));
    }
    let cancelled = false;
    axios.get(`/api/training-diary/gymnasts/${gymnastId}/summary?from=${from}&to=${to}`)
      .then(res => { if (!cancelled) { setData(res.data); setError(null); } })
      .catch(err => { if (!cancelled) setError(err.response ? 'Could not load the diary summary.' : 'The summary needs signal.'); });
    return () => { cancelled = true; };
  }, [gymnastId, range, customFrom, customTo]);

  const feel = useMemo(() => (data ? {
    xs: data.series.map(p => secs(p.date)),
    series: [
      { label: 'Energy', color: '#f39c12', values: data.series.map(p => p.energy) },
      { label: 'Mood', color: '#8e44ad', values: data.series.map(p => p.mood) },
    ],
  } : null), [data]);

  const load = useMemo(() => (data ? {
    xs: data.weeklyLoad.map(w => secs(w.weekStart)),
    series: [{ label: 'Load (RPE × min)', color: '#c2185b', values: data.weeklyLoad.map(w => w.load) }],
  } : null), [data]);

  return (
    <div className="card diary-summary">
      <div className="diary-summary__head">
        <h4 className="card-title">Summary</h4>
        <div className="diary-summary__range">
          <select aria-label="Date range" className="form-control" value={range} onChange={e => setRange(e.target.value)}>
            {RANGES.map(r => <option key={r.days} value={String(r.days)}>{r.label}</option>)}
            <option value="custom">Custom dates</option>
          </select>
          {range === 'custom' && (
            <>
              <input type="date" aria-label="From" className="form-control" value={customFrom} max={customTo} onChange={e => setCustomFrom(e.target.value)} />
              <input type="date" aria-label="To" className="form-control" value={customTo} min={customFrom} onChange={e => setCustomTo(e.target.value)} />
            </>
          )}
        </div>
      </div>
      {error && <p className="bk-error">{error}</p>}
      {data && data.sessions === 0 && <p>No sessions logged in this period.</p>}
      {data && data.sessions > 0 && (
        <>
          <dl className="diary-figures">
            <div><dt>Sessions</dt><dd>{data.sessions}</dd></div>
            <div><dt>Avg energy</dt><dd>{fmt(data.avgEnergy)}</dd></div>
            <div><dt>Avg mood</dt><dd>{fmt(data.avgMood)}</dd></div>
            <div><dt>Avg RPE</dt><dd>{fmt(data.avgRpe)}</dd></div>
          </dl>
          <DiaryChart title="Energy and mood" xs={feel.xs} series={feel.series} yRange={ENERGY_MOOD_RANGE} />
          <DiaryChart title="Weekly training load" xs={load.xs} series={load.series} />
          <table className="table diary-skill-table">
            <thead><tr><th>Skill</th><th>FIG</th><th>Reps</th><th>Sessions</th></tr></thead>
            <tbody>
              {data.skills.map(s => (
                <tr key={s.skillId || s.name}>
                  <td>{s.name}</td>
                  <td>{s.figNotation || ''}</td>
                  <td>{s.totalCount}</td>
                  <td>{s.sessions}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.routines?.length > 0 && (
            <table className="table diary-skill-table">
              <thead><tr><th>Routine</th><th>Times</th><th>Sessions</th></tr></thead>
              <tbody>
                {data.routines.map(r => (
                  <tr key={`${r.gymnastRoutineId || r.name}-${r.fromSkill}-${r.toSkill}`}>
                    <td>{routineLabel(r)}</td>
                    <td>{r.totalCount}</td>
                    <td>{r.sessions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}
